import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, PenSquare, Paperclip, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Thread } from "@/components/messages/Thread";
import { Composer } from "@/components/messages/Composer";
import { NewMessageDialog } from "@/components/messages/NewMessageDialog";
import { MessageSearch } from "@/components/messages/MessageSearch";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const short = (iso) => new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });

function ThreadList({ threads, sel, onPick }) {
  const [q, setQ] = useState("");
  const list = threads.filter((t) => `${t.name} ${t.church_name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="flex flex-col border-slate-200 lg:border-r">
      <div className="border-b border-slate-200 p-3"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or church..." className="h-10" data-testid="thread-search" /></div>
      <div className="max-h-[60vh] overflow-y-auto">
        {list.length === 0 && <p className="p-6 text-center text-sm text-slate-500" data-testid="threads-empty">No conversations yet.</p>}
        {list.map((t) => (
          <button key={t.user_id} onClick={() => onPick(t)} data-testid={`thread-item-${t.user_id}`}
            className={`flex w-full items-start gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors ${sel?.user_id === t.user_id ? "bg-rose-50" : "hover:bg-slate-50"}`}>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--gold))] font-bold text-white">{t.name?.[0]?.toUpperCase()}</span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center justify-between gap-2">
                <span className={`truncate text-sm ${t.unread ? "font-bold text-slate-900" : "font-semibold text-slate-800"}`}>{t.name}</span>
                <span className="shrink-0 text-xs text-slate-400">{t.last_at ? short(t.last_at) : ""}</span>
              </span>
              <span className="block truncate text-xs text-slate-500">{t.church_name}{t.title ? ` · ${t.title}` : ""}</span>
              <span className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1 truncate text-sm text-slate-600">{t.last_from_admin && `${t.last_sender_name}: `}{t.last_has_files && <Paperclip className="h-3 w-3 shrink-0" />}{t.last_body || (t.last_at ? "Attachment" : "Start the conversation")}</span>
                {t.unread > 0 && <span className="shrink-0 rounded-full bg-[hsl(var(--primary))] px-2 py-0.5 text-xs font-bold text-white" data-testid={`thread-unread-${t.user_id}`}>{t.unread}</span>}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function AdminMessages() {
  const { refreshUnread, isFullAdmin, user, settings } = useApp();
  const groupOn = settings?.messaging?.admin_group !== false;
  const openGroup = () => setSel(threads.find((t) => t.group) || { user_id: "adm_group", group: true, admin_thread: true, name: "All admins group", church_name: "Admin group chat" });
  const [threads, setThreads] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [compose, setCompose] = useState(false);
  const [searching, setSearching] = useState(false);
  const loadThreads = useCallback(() => api.get("/messages/threads").then((r) => setThreads(r.data)).catch((e) => toast.error(errMsg(e), { id: "msg-load-error" })), []);
  const loadThread = useCallback((uid) => api.get(`/messages/thread/${uid}`).then((r) => { setMsgs(r.data); refreshUnread(); }).catch(() => {}), [refreshUnread]);

  useEffect(() => {
    const tick = () => { if (sel) { loadThread(sel.user_id).then(loadThreads); } else { loadThreads(); } };
    tick();
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [sel, loadThreads, loadThread]);

  const reply = async (body, attachments) => {
    await api.post("/messages", sel.group ? { to: "group", body, attachments } : { to: "user", user_id: sel.peer_id || sel.user_id, body, attachments });
    toast.success("Reply sent");
    loadThread(sel.user_id).then(loadThreads);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this message for everyone?")) { return; }
    try { await api.delete(`/messages/${id}`); toast.success("Message deleted"); loadThread(sel.user_id).then(loadThreads); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-messages">
      <div className="flex flex-wrap justify-end gap-2">
        {groupOn && <Button variant="outline" onClick={openGroup} data-testid="admin-group-btn"><Users className="mr-2 h-4 w-4" /> Admin group chat</Button>}
        <Button variant="outline" onClick={() => setSearching(!searching)} data-testid="msg-search-toggle"><Search className="mr-2 h-4 w-4" /> Search messages</Button>
        <Button onClick={() => setCompose(true)} data-testid="new-message-btn"><PenSquare className="mr-2 h-4 w-4" /> New message</Button>
      </div>
      {searching && <MessageSearch onClose={() => setSearching(false)} onOpen={(r) => { const t = threads.find((x) => x.user_id === r.user_id); if (r.group) openGroup(); else setSel(t || { user_id: r.user_id, name: r.name, title: r.title, church_name: r.church_name, admin_thread: r.admin_thread, peer_id: r.peer_id }); }} />}
      <div className="grid overflow-hidden rounded-xl border border-slate-200 bg-white lg:grid-cols-[340px_1fr]">
        <div className={sel ? "hidden lg:block" : ""}><ThreadList threads={threads} sel={sel} onPick={setSel} /></div>
        <div className={sel ? "" : "hidden lg:block"}>
          {sel ? (
            <>
              <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSel(null)} data-testid="thread-back"><ArrowLeft className="h-5 w-5" /></Button>
                <div><div className="font-heading font-bold text-slate-900" data-testid="thread-title">{sel.name}</div><div className="text-xs text-slate-500">{sel.church_name}{sel.title ? ` · ${sel.title}` : ""}</div></div>
              </div>
              <Thread messages={msgs} mineIsAdmin meId={sel.admin_thread ? user?.id : null} onDelete={remove} canDeleteAll={isFullAdmin} onChanged={() => loadThread(sel.user_id).then(loadThreads)} />
              {sel.admin_thread && !sel.peer_id && !sel.group ? <p className="border-t border-slate-200 p-4 text-center text-sm text-slate-500" data-testid="admin-thread-readonly">This is a private conversation between two admins. You can read and delete messages, but not reply.</p> : <Composer onSend={reply} placeholder={`Reply to ${sel.name}...`} testid="admin-composer" />}
            </>
          ) : <p className="flex h-full min-h-72 items-center justify-center p-8 text-center text-slate-500">Pick a conversation, or press "New message".</p>}
        </div>
      </div>
      <NewMessageDialog open={compose} onClose={() => setCompose(false)} onSent={loadThreads} />
    </div>
  );
}
